/**
 * LifeRamp
 *
 * @author      Afaan Bilal
 * @copyright   LifeRamp Inc.
 *
 * Built by Eonyx Infotech LLP.
 * @link https://eonyx.io
 *
 */

import React from "react";
import { View, FlatList, StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { AntDesign } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import BouncyCheckbox from "react-native-bouncy-checkbox";

import { Colors, Gradients } from "../../../themes/colors";
import { FontSize, Size, SpacingH } from "../../../themes/sizes";
import fonts from "../../../themes/fonts";
import { GlobalStyles } from "../../../themes/styles";
import CircleButton from "../../../components/buttons/CircleButton";
import GoalCard from "../../../components/cards/GoalCard";
import PlaceHolder from "../../../components/utils/PlaceHolder";

import { Goals } from "../../../api";
import { setGoal } from "../../../state/slices/goalSlice";

export default ({ navigation }) => {
    const dispatch = useDispatch();

    const goal = useSelector(state => state.goal);
    const currentGoals = useSelector(state => state.goal.currentGoals);

    React.useEffect(() => {
        dispatch(Goals.refreshCurrentGoals());
    }, []);

    const loadMore = () => {
        if (goal.loading) return;
        goal.currentPage <= goal.currentMaxPage && dispatch(Goals.getCurrentGoals({ page: goal.currentPage }));
    };

    const viewGoal = (goal) => {
        dispatch(setGoal({ goal }));
        navigation.navigate("ViewGoal");
    };

    const editGoal = (goal) => {
        dispatch(setGoal({ goal }));
        navigation.navigate("GoalAddEdit", { edit: true })
    };

    const [filterType, setFilterType] = React.useState(["professional", "educational", "personal"]);

    const toggleFilter = (type) => {
        let categories = filterType.filter(t => t != type);

        if (!filterType.includes(type)) {
            categories = [...categories, type];
        }
        setFilterType(categories);
    };

    const filteredCurrentGoals = currentGoals.filter(g => filterType.includes(g.type));

    return (
        <View style={{ flex: 1, backgroundColor: Colors.WHITE }}>
            <FlatList
                data={filteredCurrentGoals}
                onRefresh={() => dispatch(Goals.refreshCurrentGoals())}
                refreshing={goal.refreshing || goal.loading}
                onEndReached={loadMore}
                renderItem={({ item }) => <GoalCard {...item} style={GlobalStyles.containerMarginH} onPress={() => { viewGoal(item) }} onPressEdit={() => { editGoal(item) }} />}
                ListEmptyComponent={<PlaceHolder style={GlobalStyles.containerMarginH} status={(goal.refreshing || goal.loading)} type={"regularCard"} emptyMessage={"Go ahead and add your goals"} />}
                keyExtractor={item => item.uuid}
                ListHeaderComponent={
                    <View style={[GlobalStyles.containerMarginH, {marginRight: 0, flexDirection: "row", justifyContent: "center", marginVertical: SpacingH.s2}]}>
                        <View style={styles.checkboxContainer}>
                            <BouncyCheckbox
                                size={22}
                                fillColor={Colors.TEXT_BLACK}
                                unfillColor="#FFFFFF"
                                text="Professional"
                                textStyle={styles.checkboxItem}
                                iconStyle={{ borderColor: Colors.TEXT_BLACK, borderRadius: 3, marginRight: "-9%" }}
                                disableBuiltInState
                                isChecked={filterType.includes("professional")}
                                onPress={() => { toggleFilter("professional") }}
                            />
                        </View>
                        <View style={styles.checkboxContainer}>
                            <BouncyCheckbox
                                size={22}
                                fillColor={Colors.TEXT_BLACK}
                                unfillColor="#FFFFFF"
                                text="Educational"
                                textStyle={styles.checkboxItem}
                                iconStyle={{ borderColor: Colors.TEXT_BLACK, borderRadius: 3, marginRight: "-9%" }}
                                disableBuiltInState
                                isChecked={filterType.includes("educational")}
                                onPress={() => { toggleFilter("educational") }}
                            />
                        </View>
                        <View style={styles.checkboxContainer}>
                            <BouncyCheckbox
                                size={22}
                                fillColor={Colors.TEXT_BLACK}
                                unfillColor="#FFFFFF"
                                text="Personal"
                                textStyle={styles.checkboxItem}
                                iconStyle={{ borderColor: Colors.TEXT_BLACK, borderRadius: 3, marginRight: "-9%" }}
                                disableBuiltInState
                                isChecked={filterType.includes("personal")}
                                onPress={() => { toggleFilter("personal") }}
                            />
                        </View>
                    </View>
                }
                ListFooterComponent={<View style={{ height: SpacingH.s5 }} />}
            />
            <LinearGradient start={[0.5, 0]} end={[0.5, 0.9]} colors={[Gradients.TRANSPARENT.Start, Gradients.TRANSPARENT.End]} style={{ position: "absolute", bottom: 0, left: 0, width: "100%", paddingTop: SpacingH.s0 }}>
                <CircleButton onPress={() => { navigation.navigate("GoalAddEdit", { edit: false }) }} style={GlobalStyles.circleAddButton}>
                    <AntDesign name="plus" size={36} color={Colors.NAVY} />
                </CircleButton>
            </LinearGradient>
        </View>
    );
};

const styles = StyleSheet.create({
    checkboxContainer: {
        flexDirection: "row",
        alignItems: "center",
        width: Size.w30,
    },
    checkboxItem: {
        fontSize: FontSize.SMALLER,
        fontFamily: fonts.MainMedium,
        color: Colors.TEXT_BLACK,
        textDecorationLine: "none",
    },
});
